/* ==========================================================================
   custom-sections.js — My Perfect Summer
   --------------------------------------------------------------------------
   JavaScript des sections sur mesure. Vanilla uniquement, aucune
   bibliotheque externe.

   POURQUOI DES "CUSTOM ELEMENTS" ?
   Un custom element est une balise HTML que l'on invente soi-meme, par
   exemple <mps-announcement>. Le navigateur previent automatiquement le
   code quand cette balise apparait dans la page (connectedCallback) et
   quand elle disparait (disconnectedCallback).

   L'interet : dans l'editeur de theme Shopify, les sections sont
   reconstruites en permanence pendant que l'on modifie les reglages. Avec
   un custom element, tout se remet en route tout seul. Sans, il faudrait
   ecouter des evenements Shopify a la main et l'apercu finirait par casser.

   SOMMAIRE
     1. <mps-announcement>  barre d'annonce a messages tournants
     2. <mps-gallery>       galerie produit avec miniatures
     3. <mps-variants>      selecteur de taille et de cote
     4. <mps-bundle>        selecteur de pack (quantite + remise)
     5. <mps-carousel>      carrousel d'avis (page d'accueil)
     6. <mps-sticky>        barre d'achat collante (mobile)
     7. Ajout au panier depuis la zone d'achat
     8. Apparition au scroll (fade-in)
   ========================================================================== */

(function () {
  'use strict';

  /* La personne a-t-elle demande a son systeme de reduire les animations ?
     Certaines personnes ont des vertiges avec le mouvement : on respecte
     ce reglage partout. */
  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;


  /* ========================================================================
     1. <mps-announcement> — BARRE D'ANNONCE
     ------------------------------------------------------------------------
     Fait defiler plusieurs messages, un a la fois.

     Regle d'accessibilite appliquee (WCAG 2.2.2) : tout contenu qui bouge
     tout seul doit pouvoir etre mis en pause. La rotation s'arrete donc
     au survol de la souris et quand on navigue au clavier dedans.
     ======================================================================== */

  class MpsAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-mps-message]'));

      /* Un seul message : rien a faire tourner. */
      if (this.items.length < 2) return;

      /* L'intervalle est defini en Liquid via data-interval (millisecondes). */
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.index = 0;
      this.timer = null;

      /* On memorise les fonctions liees pour pouvoir les retirer proprement
         plus tard dans disconnectedCallback. */
      this.onPause = this.stop.bind(this);
      this.onResume = this.start.bind(this);
      this.onVisibility = this.handleVisibility.bind(this);

      this.addEventListener('mouseenter', this.onPause);
      this.addEventListener('mouseleave', this.onResume);
      this.addEventListener('focusin', this.onPause);
      this.addEventListener('focusout', this.onResume);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.show(0);
      this.start();
    }

    disconnectedCallback() {
      /* Menage : sans ca, les minuteurs continueraient de tourner dans le
         vide et s'accumuleraient a chaque modification dans l'editeur. */
      this.stop();
      this.removeEventListener('mouseenter', this.onPause);
      this.removeEventListener('mouseleave', this.onResume);
      this.removeEventListener('focusin', this.onPause);
      this.removeEventListener('focusout', this.onResume);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* Affiche le message numero i et masque tous les autres.
       L'attribut hidden masque a la fois a l'ecran et pour les lecteurs
       d'ecran : ils ne liront donc que le message reellement visible. */
    show(i) {
      this.items.forEach(function (item, n) {
        var isActive = n === i;
        item.hidden = !isActive;
        item.classList.toggle('is-active', isActive);
      });
      this.index = i;
    }

    next() {
      /* Le modulo (%) fait revenir au premier message apres le dernier. */
      this.show((this.index + 1) % this.items.length);
    }

    start() {
      this.stop();
      this.timer = setInterval(this.next.bind(this), this.interval);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    /* Onglet passe en arriere-plan : on met en pause pour economiser la
       batterie du telephone. */
    handleVisibility() {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    }
  }

  if (!customElements.get('mps-announcement')) {
    customElements.define('mps-announcement', MpsAnnouncement);
  }


  /* ========================================================================
     2. <mps-gallery> — GALERIE PRODUIT
     ------------------------------------------------------------------------
     Les images sont posees cote a cote dans un conteneur qui defile
     horizontalement, avec "scroll-snap" en CSS : le navigateur cale
     automatiquement l'image sur le bord. Le glissement au doigt sur mobile
     est donc gere par le navigateur lui-meme, sans code tactile a ecrire.

     Ce JavaScript ne fait que deux choses :
       - cliquer sur une miniature fait defiler jusqu'a l'image
       - faire defiler met en surbrillance la bonne miniature
     ======================================================================== */

  class MpsGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-mps-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-mps-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-mps-thumb]'));

      if (!this.viewport || this.slides.length < 2) return;

      this.onThumbClick = this.handleThumbClick.bind(this);
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', this.onThumbClick);
      }, this);

      /* On surveille quelle image est au centre du cadre.
         IntersectionObserver evite d'ecouter l'evenement scroll en
         continu, ce qui serait bien plus lourd pour le telephone. */
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          this.handleSlideVisible.bind(this),
          { root: this.viewport, threshold: 0.6 }
        );
        this.slides.forEach(function (slide) {
          this.observer.observe(slide);
        }, this);
      }
    }

    disconnectedCallback() {
      this.thumbs.forEach(function (thumb) {
        thumb.removeEventListener('click', this.onThumbClick);
      }, this);
      if (this.observer) this.observer.disconnect();
    }

    handleThumbClick(event) {
      var index = parseInt(event.currentTarget.dataset.mpsThumb, 10);
      var slide = this.slides[index];
      if (!slide) return;

      /* scrollTo plutot que scrollIntoView : scrollIntoView ferait aussi
         defiler la page entiere, ce qui ferait sauter l'ecran. */
      this.viewport.scrollTo({
        left: slide.offsetLeft - this.viewport.offsetLeft,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });

      this.setActive(index);
    }

    handleSlideVisible(entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        this.setActive(this.slides.indexOf(entry.target));
      }, this);
    }

    setActive(index) {
      this.thumbs.forEach(function (thumb, n) {
        var isActive = n === index;
        thumb.classList.toggle('is-active', isActive);
        /* aria-current annonce aux lecteurs d'ecran quelle image est
           actuellement affichee. */
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }
  }

  if (!customElements.get('mps-gallery')) {
    customElements.define('mps-gallery', MpsGallery);
  }


  /* ========================================================================
     3. <mps-variants> — SELECTEUR DE TAILLE ET DE COTE
     ------------------------------------------------------------------------
     Quand on clique sur une taille ou un cote, on cherche la variante
     correspondante dans les donnees deposees par le Liquid, puis on met a
     jour la page sans la recharger :
       - le champ cache "id" du formulaire (c'est lui qui part au panier)
       - le prix, le prix barre et le badge de remise
       - l'etat du bouton d'ajout au panier
       - l'URL, pour que la page reste partageable

     Si la combinaison choisie n'existe pas dans le catalogue, le bouton est
     desactive et un message l'explique.
     ======================================================================== */

  class MpsVariants extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('[data-mps-variant-data]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        /* Donnees illisibles : on ne fait rien plutot que de casser la
           page. Le formulaire continue de fonctionner avec la variante
           choisie par defaut cote serveur. */
        return;
      }

      this.radios = Array.from(this.querySelectorAll('[data-mps-option-index]'));
      if (!this.radios.length) return;

      this.variantInput = this.querySelector('[data-mps-variant-input]');
      this.priceEl = this.querySelector('[data-mps-price]');
      this.compareEl = this.querySelector('[data-mps-compare-price]');
      this.discountEl = this.querySelector('[data-mps-discount]');
      this.atcEl = this.querySelector('[data-mps-atc]');
      this.unavailableEl = this.querySelector('[data-mps-unavailable]');
      this.chosenEls = Array.from(this.querySelectorAll('[data-mps-chosen]'));

      this.onChange = this.handleChange.bind(this);
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    handleChange(event) {
      /* On ne reagit qu'aux boutons radio du selecteur, pas aux autres
         champs qui pourraient exister dans le formulaire. */
      if (!event.target.matches('[data-mps-option-index]')) return;
      this.update();
    }

    /* Renvoie les valeurs cochees, dans l'ordre des options du produit.
       Exemple : ["L (65-85 kg)", "Derecha"] */
    getSelection() {
      var values = [];
      this.radios.forEach(function (radio) {
        if (radio.checked) {
          values[parseInt(radio.dataset.mpsOptionIndex, 10)] = radio.value;
        }
      });
      return values;
    }

    /* Cherche la variante dont toutes les options correspondent. */
    findVariant(selection) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (option, i) {
          return option === selection[i];
        });
      });
    }

    update() {
      var selection = this.getSelection();
      var variant = this.findVariant(selection);

      /* Rappel de l'option choisie a cote du titre du groupe. */
      this.chosenEls.forEach(function (el) {
        var i = parseInt(el.dataset.mpsChosen, 10);
        el.textContent = selection[i] || '';
      });

      if (!variant) {
        this.setUnavailable(true);
        return;
      }

      this.setUnavailable(false);

      if (this.variantInput) this.variantInput.value = variant.id;

      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateUrl(variant);

      /* Signal a l'attention d'autres composants (barre d'achat collante
         par exemple) : ils peuvent l'ecouter pour se synchroniser. */
      this.dispatchEvent(new CustomEvent('mps:variant:change', {
        bubbles: true,
        detail: { variant: variant }
      }));
    }

    updatePrice(variant) {
      if (this.priceEl) this.priceEl.textContent = variant.price;

      /* Une variante est en promotion si son prix barre est renseigne ET
         reellement superieur au prix de vente. */
      var onSale = variant.compare_at_raw > variant.price_raw;

      if (this.compareEl) {
        this.compareEl.textContent = variant.compare_at || '';
        this.compareEl.hidden = !onSale;
      }

      if (this.discountEl) {
        if (onSale) {
          var pct = Math.round(
            (variant.compare_at_raw - variant.price_raw) / variant.compare_at_raw * 100
          );
          /* − est le vrai signe moins typographique, plus lisible
             qu'un trait d'union. */
          this.discountEl.textContent = '−' + pct + ' %';
        }
        this.discountEl.hidden = !onSale;
      }
    }

    updateButton(variant) {
      if (!this.atcEl) return;
      this.atcEl.disabled = !variant.available;
      this.atcEl.textContent = variant.available
        ? this.atcEl.dataset.labelAvailable
        : this.atcEl.dataset.labelSoldOut;
    }

    /* Ajoute ?variant=... dans la barre d'adresse sans recharger la page,
       pour que le lien copie-colle ouvre la bonne taille. */
    updateUrl(variant) {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    setUnavailable(isUnavailable) {
      if (this.unavailableEl) this.unavailableEl.hidden = !isUnavailable;
      if (this.atcEl) {
        this.atcEl.disabled = isUnavailable;
        if (isUnavailable) {
          this.atcEl.textContent = this.atcEl.dataset.labelSoldOut;
        }
      }
    }
  }

  if (!customElements.get('mps-variants')) {
    customElements.define('mps-variants', MpsVariants);
  }


  /* ========================================================================
     4. <mps-bundle> — SELECTEUR DE PACK
     ------------------------------------------------------------------------
     Trois cartes : 1 unite, 2 unites, 3 unites. Chaque carte porte une
     quantite et un pourcentage de remise.

     Deux responsabilites, et deux seulement :
       - ecrire la quantite choisie dans le champ cache du formulaire,
         c'est lui qui part au panier
       - reafficher les prix de chaque carte quand la variante change
         (une taille plus chere renchérit tous les packs)

     ATTENTION — ce composant AFFICHE une remise, il ne l'APPLIQUE pas.
     Shopify calcule le panier a partir du prix de la variante multiplie
     par la quantite. Pour que le total au paiement corresponde vraiment
     a ce qui est affiche ici, il faut creer la reduction automatique
     correspondante dans l'admin (Reductions > Reduction automatique,
     sur la quantite). Sans cela, le client verrait −30 % sur la page et
     paierait le plein tarif : c'est le genre d'ecart qui fait annuler
     une commande et perdre la confiance.
     ======================================================================== */

  class MpsBundle extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.cards = Array.from(this.querySelectorAll('.mps-bundle__card'));
      this.inputs = Array.from(this.querySelectorAll('[data-mps-tier]'));

      if (!this.inputs.length) return;

      this.qtyInput = this.form
        ? this.form.querySelector('[data-mps-quantity]')
        : null;

      /* Prix d'une unite, en centimes. Reactualise a chaque changement de
         variante par l'evenement mps:variant:change. */
      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.unitCompare = parseInt(this.dataset.unitCompare, 10) || 0;

      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'es-ES';
      /* Format monetaire de la boutique (ex. « {{amount_with_comma_separator}}€ ») :
         le JavaScript affiche les prix exactement comme le Liquid. */
      this.moneyFormat = this.dataset.moneyFormat || '';

      /* Catalogue des variantes, depose par le Liquid. Il sert a retrouver
         l'identifiant correspondant a une taille et un cote choisis dans
         les menus deroulants d'une unite. */
      this.variants = [];
      var dataEl = this.form && this.form.querySelector('[data-mps-variant-data]');
      if (dataEl) {
        try {
          this.variants = JSON.parse(dataEl.textContent);
        } catch (e) {
          this.variants = [];
        }
      }

      this.variantInput = this.form
        ? this.form.querySelector('[data-mps-variant-input]')
        : null;

      this.onChange = this.handleChange.bind(this);
      this.onVariant = this.handleVariant.bind(this);

      this.addEventListener('change', this.onChange);
      /* L'evenement est emis par <mps-variants>, un frere dans le meme
         formulaire : on ecoute donc sur le document, ou il remonte. */
      document.addEventListener('mps:variant:change', this.onVariant);

      /* L'envoi du formulaire n'est PAS intercepte ici : c'est le module 7
         qui s'en charge, pour que l'ajout au panier fonctionne de la meme
         facon avec ou sans packs. Ce composant se contente de fournir les
         lignes a ajouter via buildItems(). */

      /* Etat initial : on aligne la quantite sur la carte deja cochee. */
      this.syncQuantity();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('mps:variant:change', this.onVariant);
    }

    handleChange(event) {
      if (event.target.matches('[data-mps-tier]')) {
        this.syncQuantity();
        return;
      }

      /* Un menu deroulant d'unite modifie a la main : on le marque, pour
         ne plus l'ecraser quand la personne rechange la taille en haut. */
      if (event.target.matches('[data-mps-unit-select]')) {
        event.target.dataset.touched = 'true';
      }
    }

    handleVariant(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      this.unitPrice = variant.price_raw;
      /* Pas de prix barre sur cette variante : on retombe sur le prix de
         vente, sinon l'economie affichee serait absurde. */
      this.unitCompare = variant.compare_at_raw > variant.price_raw
        ? variant.compare_at_raw
        : variant.price_raw;

      this.renderPrices();
      this.syncUnitsToVariant(variant);
    }

    /* Quand on change la taille dans les pilules du haut, les unites du
       pack suivent — sauf celles que la personne a deja reglees elle-meme.
       Sans cela, choisir XL en haut puis un pack de deux ajouterait deux
       genouilleres en M : surprise garantie a la reception. */
    syncUnitsToVariant(variant) {
      if (!variant || !variant.options) return;

      this.querySelectorAll('[data-mps-unit-select]').forEach(function (select) {
        if (select.dataset.touched === 'true') return;

        var i = parseInt(select.dataset.optionIndex, 10);
        var value = variant.options[i];
        if (value != null) select.value = value;
      });
    }

    /* Ecrit la quantite du palier coche dans le champ cache et met a jour
       l'habillage des cartes. */
    syncQuantity() {
      var checked = this.inputs.find(function (input) {
        return input.checked;
      });

      if (!checked) return;

      if (this.qtyInput) {
        this.qtyInput.value = checked.dataset.qty || '1';
      }

      this.cards.forEach(function (card) {
        var input = card.querySelector('[data-mps-tier]');
        var selected = !!input && input.checked;
        card.classList.toggle('is-selected', selected);

        /* Les menus par unite n'apparaissent que dans la carte cochee :
           afficher ceux des trois packs a la fois noierait la personne
           sous une dizaine de listes deroulantes. */
        var units = card.querySelector('[data-mps-units]');
        if (units) units.hidden = !selected;
      });
    }

    /* Retrouve une variante a partir des valeurs choisies, par exemple
       ["L (65-85 kg)", "Derecha"]. */
    findVariant(values) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (option, i) {
          return option === values[i];
        });
      });
    }

    /* Construit la liste des lignes a ajouter au panier.

       Trois retours possibles, et la distinction compte :
         - un tableau : les lignes a ajouter
         - undefined  : aucun pack coche, ce composant n'a rien a dire,
                        l'appelant se rabat sur la variante simple
         - null       : une combinaison choisie n'existe pas ou est
                        epuisee. On refuse l'ajout plutot que de creer
                        une commande impossible a honorer. */
    buildItems() {
      var checked = this.inputs.find(function (input) { return input.checked; });
      if (!checked) return undefined;

      var qty = parseInt(checked.dataset.qty, 10) || 1;
      var card = checked.closest('.mps-bundle__card');
      var selects = card
        ? Array.from(card.querySelectorAll('[data-mps-unit-select]'))
        : [];

      /* Carte sans menus (pack d'une unite en mise en page standard, ou
         produit sans options) : on prend la variante choisie par les
         pilules du haut. Si la carte a ses propres menus, meme pour une
         seule unite, ce sont eux qui decident. */
      if (!selects.length) {
        var id = this.variantInput && parseInt(this.variantInput.value, 10);
        if (!id) return null;
        return [{ id: id, quantity: qty }];
      }

      /* Pack multi-unites : une ligne par unite, chacune avec sa propre
         variante. Shopify fusionnerait deux lignes identiques (meme taille,
         meme cote) en une seule de quantite 2 : la propriete cachee
         _unidad les rend distinctes, pour que le panier montre une ligne
         par genouillere. Le tiret bas la masque au client sur la page de
         paiement. */
      var byUnit = {};
      selects.forEach(function (select) {
        var unit = select.dataset.unit;
        if (!byUnit[unit]) byUnit[unit] = [];
        byUnit[unit][parseInt(select.dataset.optionIndex, 10)] = select.value;
      });

      var items = [];
      var ok = true;

      var units = Object.keys(byUnit);

      units.forEach(function (unit, n) {
        var variant = this.findVariant(byUnit[unit]);
        if (!variant || !variant.available) {
          ok = false;
          return;
        }
        var line = { id: variant.id, quantity: 1 };
        /* Le pack d'une unite ne change pas : pas de propriete. */
        if (units.length > 1) line.properties = { _unidad: String(n + 1) };
        items.push(line);
      }, this);

      /* Envoyees dans l'ordre 1 puis 2 : Shopify place la derniere ajoutee
         en tete du panier, et c'est sur cette ligne de tete que la
         reduction « Achetez X, obtenez Y » s'applique. L'unite 2 porte donc
         l'offre, comme sur la carte du pack. Le tiroir panier affiche les
         lignes de la plus ancienne a la plus recente (snippets/cart-drawer.liquid),
         d'ou l'ordre Unidad 1 puis Unidad 2. */

      return ok ? items : null;
    }

    formatMoney(cents, dropZeros) {
      /* Format de la boutique en priorite, pour afficher « 49,90€ » comme le
         Liquid et non « 49,90 € » comme le navigateur. dropZeros retire
         « ,00 » (equivalent de money_without_trailing_zeros). */
      var fmt = this.moneyFormat;
      var m = fmt && fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      if (m) {
        var value = (cents / 100).toFixed(2);
        var parts = value.split('.');
        var thousands = m[1] === 'amount_with_comma_separator' ? '.' : ',';
        var decimals = m[1] === 'amount_with_comma_separator' ? ',' : '.';
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        var amount = (dropZeros && parts[1] === '00') || /no_decimals/.test(m[1])
          ? whole
          : whole + decimals + parts[1];
        return fmt.replace(m[0], amount);
      }
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      } catch (e) {
        /* Devise ou langue inconnue du navigateur : on affiche au moins
           un nombre lisible plutot que de casser la page. */
        return (cents / 100).toFixed(2);
      }
    }

    renderPrices() {
      this.inputs.forEach(function (input) {
        var card = input.closest('.mps-bundle__card');
        if (!card) return;

        var qty = parseInt(input.dataset.qty, 10) || 1;
        var discount = parseInt(input.dataset.discount, 10) || 0;

        /* Meme calcul que dans le Liquid, et surtout que dans le panier
           Shopify : remise calculee par unite et TRONQUEE au centime
           inferieur, puis retranchee du prix. Sans cela, le prix annonce
           ici differerait de un a deux centimes du montant facture. */
        var total;
        /* Prix de l'unite en offre : prix normal par defaut (aucune remise). */
        var offer = this.unitPrice;
        if (input.dataset.offerPrice !== undefined) {
          /* Une unite du pack a prix fixe, une seule fois : meme calcul que
             la reduction « Achetez X, obtenez Y » de l'admin. */
          offer = Math.min(parseInt(input.dataset.offerPrice, 10) || 0, this.unitPrice);
          total = this.unitPrice * qty - this.unitPrice + offer;
        } else if (input.dataset.discountAmount !== undefined) {
          /* Remise en montant fixe sur le pack, comme la reduction
             automatique Shopify « une fois par commande » : elle est
             retranchee du total, pas de chaque unite. */
          var amount = parseInt(input.dataset.discountAmount, 10) || 0;
          total = Math.max(0, this.unitPrice * qty - amount);
        } else {
          var unitDisc = Math.floor(this.unitPrice * discount / 100);
          var unitFinal = this.unitPrice - unitDisc;
          total = unitFinal * qty;
        }
        /* A l'unite : le prix barre de la fiche. En pack : quantite x prix
           de vente unitaire, meme regle que dans le Liquid. */
        var compare = qty === 1 ? this.unitCompare : this.unitPrice * qty;

        var totalEl = card.querySelector('[data-mps-tier-total]');
        var compareEl = card.querySelector('[data-mps-tier-compare]');
        var saveEl = card.querySelector('[data-mps-tier-save]');

        if (totalEl) totalEl.textContent = this.formatMoney(total);

        /* Prix par unite, meme calcul que le Liquid : arrondi au centime
           superieur, jamais plus bas que le vrai. */
        var unitEl = card.querySelector('[data-mps-tier-unit]');
        if (unitEl && qty > 1) {
          unitEl.textContent = unitEl.dataset.template
            .replace('[precio]', this.formatMoney(Math.ceil(total / qty)));
        }

        if (compareEl) {
          compareEl.textContent = this.formatMoney(compare);
          compareEl.hidden = compare <= total;
        }

        /* Meme regle que dans le Liquid : pourcentage arrondi a l'entier le
           plus proche. Si les deux calculs divergeaient, le chiffre
           changerait au moment ou le client choisit une autre taille. */
        var saved = Math.max(0, compare - total);
        var pct = compare > 0 ? Math.round(saved * 100 / compare) : 0;
        var fill = function (tpl) {
          return tpl
            .replace('[precio_oferta]', this.formatMoney(offer, true))
            .replace('[ahorro_pct]', String(pct))
            .replace('[ahorro]', this.formatMoney(saved));
        }.bind(this);

        if (saveEl && saveEl.dataset.template) {
          saveEl.textContent = fill(saveEl.dataset.template);
          saveEl.hidden = saved <= 0;
        }

        var subEl = card.querySelector('[data-mps-tier-subtitle]');
        if (subEl && subEl.dataset.template) subEl.textContent = fill(subEl.dataset.template);

        /* Prix sous chaque vignette : prix normal, et prix d'offre pour
           l'unite qui en beneficie. */
        card.querySelectorAll('[data-mps-unit-amount]').forEach(function (el) {
          el.textContent = this.formatMoney(el.dataset.mpsUnitAmount === 'offer' ? offer : this.unitPrice);
        }, this);
      }, this);
    }
  }

  if (!customElements.get('mps-bundle')) {
    customElements.define('mps-bundle', MpsBundle);
  }


  /* ========================================================================
     5. <mps-carousel> — CARROUSEL D'AVIS
     ------------------------------------------------------------------------
     Le defilement lui-meme est fait par le CSS (scroll-snap) : glisser au
     doigt fonctionne donc sans une ligne de JavaScript, et sans lui les
     cartes restent parcourables.

     Ce composant n'ajoute que le confort a la souris :
       - deux fleches qui font defiler d'une carte
       - les fleches sont masquees s'il n'y a rien a faire defiler
       - une fleche est desactivee quand on est au bout
     ======================================================================== */

  class MpsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mps-track]');
      this.nav = this.querySelector('[data-mps-nav]');
      this.prev = this.querySelector('[data-mps-prev]');
      this.next = this.querySelector('[data-mps-next]');

      if (!this.track || !this.nav) return;

      this.onPrev = this.scrollByCard.bind(this, -1);
      this.onNext = this.scrollByCard.bind(this, 1);
      this.onScroll = this.updateArrows.bind(this);
      this.onResize = this.refresh.bind(this);

      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.refresh();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    /* Y a-t-il vraiment de quoi defiler ? Une tolerance de 4 px evite que
       des arrondis de sous-pixel fassent apparaitre les fleches pour rien. */
    get scrollable() {
      return this.track.scrollWidth - this.track.clientWidth > 4;
    }

    refresh() {
      this.nav.hidden = !this.scrollable;
      this.updateArrows();
    }

    scrollByCard(direction) {
      var card = this.track.querySelector('[data-mps-slide]');
      /* Largeur d'une carte plus l'espace qui la suit : on retombe ainsi
         pile sur la carte voisine, quel que soit le nombre visible. */
      var step = card
        ? card.getBoundingClientRect().width + 16
        : this.track.clientWidth;

      this.track.scrollBy({
        left: step * direction,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    updateArrows() {
      if (!this.scrollable) return;

      var max = this.track.scrollWidth - this.track.clientWidth;
      var x = this.track.scrollLeft;

      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
    }
  }

  if (!customElements.get('mps-carousel')) {
    customElements.define('mps-carousel', MpsCarousel);
  }


  /* ========================================================================
     6. <mps-sticky> — BARRE D'ACHAT COLLANTE (MOBILE)
     ------------------------------------------------------------------------
     La barre se montre quand la zone d'achat quitte l'ecran, et se cache
     des qu'on y revient. On surveille la cible avec IntersectionObserver
     plutot qu'en ecoutant le scroll : le navigateur fait le travail, sans
     calcul a chaque pixel parcouru.

     Le prix n'est jamais recalcule ici : il est recopie depuis la zone
     d'achat. Deux calculs paralleles finiraient tot ou tard par diverger,
     et afficher deux prix sur le meme ecran est la pire chose a faire sur
     une page produit.
     ======================================================================== */

  class MpsSticky extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector(this.dataset.target || '#mps-buy');
      this.cta = this.querySelector('[data-mps-sticky-cta]');
      this.priceEl = this.querySelector('[data-mps-sticky-price]');
      this.compareEl = this.querySelector('[data-mps-sticky-compare]');

      /* Sans zone d'achat sur la page, la barre n'a pas de sens. */
      if (!this.target) return;

      this.onClick = this.handleClick.bind(this);
      this.onSync = this.refreshPrice.bind(this);

      if (this.cta) this.cta.addEventListener('click', this.onClick);

      /* Un changement de taille ou de pack doit se refleter dans la barre. */
      document.addEventListener('mps:variant:change', this.onSync);
      document.addEventListener('change', this.onSync);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          this.handleIntersect.bind(this),
          { threshold: 0 }
        );
        this.observer.observe(this.target);
      } else {
        /* Navigateur trop ancien : on laisse la barre masquee plutot que
           de l'afficher en permanence par-dessus le contenu. */
        this.hidden = true;
      }

      this.refreshPrice();
    }

    disconnectedCallback() {
      if (this.cta) this.cta.removeEventListener('click', this.onClick);
      document.removeEventListener('mps:variant:change', this.onSync);
      document.removeEventListener('change', this.onSync);
      if (this.observer) this.observer.disconnect();
    }

    handleIntersect(entries) {
      var entry = entries[0];
      if (!entry) return;

      /* On n'affiche la barre que si la zone d'achat est passee AU-DESSUS
         de l'ecran. Sinon elle apparaitrait aussi avant d'y arriver, alors
         qu'on n'a encore rien vu du produit. */
      var passedAbove = entry.boundingClientRect.bottom < 0;
      var show = !entry.isIntersecting && passedAbove;

      this.hidden = !show;
      this.classList.toggle('is-visible', show);
    }

    /* Recopie le montant reellement affiche dans la zone d'achat :
       le total du pack choisi s'il y en a un, sinon le prix unitaire. */
    refreshPrice() {
      if (!this.priceEl) return;

      var source =
        document.querySelector('.mps-bundle__card.is-selected [data-mps-tier-total]') ||
        document.querySelector('[data-mps-price]');

      if (source) this.priceEl.textContent = source.textContent.trim();

      /* Prix barre : meme logique, recopie depuis la carte de pack choisie
         ou le prix barre de la zone d'achat. Masque s'il n'y en a pas. */
      if (!this.compareEl) return;
      var compare =
        document.querySelector('.mps-bundle__card.is-selected [data-mps-tier-compare]') ||
        document.querySelector('[data-mps-compare-price]');
      var text = compare && !compare.hidden ? compare.textContent.trim() : '';
      this.compareEl.textContent = text;
      this.compareEl.hidden = !text;

      /* Prix par unite : seulement si le pack choisi en affiche un. */
      var unitEl = this.querySelector('[data-mps-sticky-unit]');
      if (!unitEl) return;
      var unit = document.querySelector('.mps-bundle__card.is-selected [data-mps-tier-unit]');
      var unitText = unit ? unit.textContent.trim() : '';
      unitEl.textContent = unitText;
      unitEl.hidden = !unitText;
    }

    handleClick() {
      if (this.dataset.action === 'add') {
        var form = this.target.querySelector('form');
        /* requestSubmit declenche les gestionnaires "submit" du
           formulaire — dont celui de <mps-bundle>, qui sait construire un
           panier multi-lignes. form.submit() les contournerait et casserait
           les packs. */
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit();
          return;
        }
      }

      this.target.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    }
  }

  if (!customElements.get('mps-sticky')) {
    customElements.define('mps-sticky', MpsSticky);
  }


  /* ========================================================================
     7. AJOUT AU PANIER DEPUIS LA ZONE D'ACHAT
     ------------------------------------------------------------------------
     Un seul chemin d'ajout au panier, quel que soit le contenu de la page :
     on intercepte l'envoi du formulaire, on poste sur /cart/add.js, puis on
     rafraichit le tiroir sans recharger la page.

     POURQUOI ICI ET PLUS DANS <mps-bundle> ?
     L'interception vivait dans le selecteur de packs. Consequence : retirer
     les packs de la zone d'achat, ou n'en presélectionner aucun, faisait
     repasser l'ajout en envoi de formulaire classique — rechargement complet
     et tiroir qui ne s'ouvre plus. Le comportement dependait donc d'un bloc
     optionnel. Il est desormais porte par le formulaire lui-meme.

     Les packs restent la source des lignes a ajouter quand ils existent :
     ce module leur demande simplement buildItems().
     ======================================================================== */

  var MpsCart = {

    /* Quelles lignes envoyer au panier ?
       On interroge d'abord le selecteur de packs. S'il n'a rien a dire
       (aucun pack sur la page, ou aucun coche), on retombe sur la variante
       choisie par les pilules et la quantite du formulaire. */
    itemsFor: function (form) {
      var bundle = form.querySelector('mps-bundle');

      if (bundle && typeof bundle.buildItems === 'function') {
        var fromBundle = bundle.buildItems();
        /* null = combinaison inexistante ou epuisee : on refuse l'ajout.
           undefined = le composant ne se prononce pas, on continue. */
        if (fromBundle === null) return null;
        if (fromBundle) return fromBundle;
      }

      var input = form.querySelector('[data-mps-variant-input]');
      var qtyEl = form.querySelector('[data-mps-quantity]');
      var id = input && parseInt(input.value, 10);
      if (!id) return null;

      return [{ id: id, quantity: (qtyEl && parseInt(qtyEl.value, 10)) || 1 }];
    },

    busy: function (form, isBusy) {
      var atc = form.querySelector('[data-mps-atc]');
      if (!atc) return;
      atc.disabled = isBusy;
      atc.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    },

    error: function (form, show) {
      var el = form.querySelector('[data-mps-cart-error]');
      if (!el) return;
      if (show && !el.textContent.trim()) {
        var bundle = form.querySelector('mps-bundle');
        el.textContent = (bundle && bundle.dataset.errorText) || '';
      }
      el.hidden = !show;
    },

    submit: function (form, event) {
      var items = MpsCart.itemsFor(form);

      /* Rien d'exploitable : on laisse le navigateur envoyer le formulaire
         normalement plutot que de bloquer la vente. */
      if (!items) {
        if (form.querySelector('mps-bundle')) {
          event.preventDefault();
          MpsCart.error(form, true);
        }
        return;
      }

      event.preventDefault();
      MpsCart.error(form, false);
      MpsCart.busy(form, true);

      var body = { items: items };

      /* On demande au passage le HTML du tiroir et de la pastille du
         header : Shopify les renvoie deja rendus, il n'y a plus qu'a les
         injecter. */
      var drawer = document.querySelector('cart-drawer');
      var sections = null;
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      var url = (window.routes && window.routes.cart_add_url) || '/cart/add';

      fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message);
            return data;
          });
        })
        .then(function (data) {
          if (drawer && sections && data.sections) {
            drawer.renderContents(data);

            /* Dawn pose la classe is-empty sur <cart-drawer> au rendu
               initial, et ne la retire que depuis product-form.js — un
               fichier absent de nos pages, puisque nos gabarits n'utilisent
               aucune section produit de Dawn. Sans ce nettoyage, le
               formulaire du tiroir reste en display:none : le client voit
               un tiroir vide alors que son article est bien au panier. */
            drawer.classList.remove('is-empty');

            if (typeof drawer.open === 'function') drawer.open();
          } else {
            /* Pas de tiroir sur ce theme : on bascule sur la page panier,
               qui existe toujours. */
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function () {
          MpsCart.error(form, true);
        })
        .finally(function () {
          MpsCart.busy(form, false);
        });
    }
  };

  function initBuyboxCart() {
    var forms = document.querySelectorAll('form.mps-buybox__form:not([data-mps-cart-bound])');
    forms.forEach(function (form) {
      /* Marqueur : dans l'editeur de theme la section est reconstruite en
         permanence, sans ce garde-fou on empilerait les gestionnaires. */
      form.dataset.mpsCartBound = 'true';
      form.addEventListener('submit', function (event) {
        MpsCart.submit(form, event);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBuyboxCart);
  } else {
    initBuyboxCart();
  }
  document.addEventListener('shopify:section:load', initBuyboxCart);


  /* ========================================================================
     8. APPARITION AU SCROLL (FADE-IN)
     ------------------------------------------------------------------------
     Les elements portant la classe .mps-reveal sont invisibles au depart
     (voir le CSS). Des qu'ils entrent dans l'ecran, on leur ajoute la
     classe .is-visible et le CSS s'occupe du fondu.

     IntersectionObserver est l'outil natif du navigateur pour detecter
     qu'un element entre dans l'ecran. Il est bien plus econome que
     d'ecouter l'evenement scroll en permanence.
     ======================================================================== */

  function initReveal() {
    var elements = document.querySelectorAll('.mps-reveal:not(.is-visible)');
    if (!elements.length) return;

    /* Animations reduites, ou navigateur trop ancien : on affiche tout
       immediatement, sans fondu. Mieux vaut un contenu visible sans effet
       qu'un contenu invisible. */
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          /* Une fois l'element apparu, on arrete de le surveiller :
             l'animation ne se joue qu'une seule fois. */
          observer.unobserve(entry.target);
        });
      },
      {
        /* Declenche le fondu quand l'element est a 80 px du bas de l'ecran,
           pour qu'il soit deja apparu au moment ou on le regarde. */
        rootMargin: '0px 0px -80px 0px',
        threshold: 0
      }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* Au chargement de la page. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  /* Dans l'editeur de theme : quand on ajoute ou modifie une section,
     Shopify emet cet evenement. On relance la detection sur le nouveau
     contenu. */
  document.addEventListener('shopify:section:load', initReveal);
})();


/* ==========================================================================
   9. LECTURE DES VIDEOS A L'ECRAN
   --------------------------------------------------------------------------
   Les videos marquees data-mps-autoplay (section mps-video-strip) ne
   tournent que lorsqu'elles sont visibles, et s'arretent quand on les
   quitte : la page ne telecharge ainsi que ce que le client regarde.

   Si le systeme demande de reduire les animations, rien ne se lance tout
   seul : le client peut toujours toucher une video pour la demarrer.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initAutoplay() {
    var videos = document.querySelectorAll('video[data-mps-autoplay]:not([data-mps-bound])');
    if (!videos.length) return;

    videos.forEach(function (video) {
      video.dataset.mpsBound = 'true';
      /* Toucher la video la met en pause ou la relance. */
      video.addEventListener('click', function () {
        if (video.paused) { video.play().catch(function () {}); } else { video.pause(); }
      });
    });

    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          /* play() renvoie une promesse rejetee si le navigateur refuse la
             lecture automatique : on l'ignore, la video reste simplement
             sur sa premiere image. */
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.5 });

    videos.forEach(function (video) { observer.observe(video); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAutoplay);
  } else {
    initAutoplay();
  }
  document.addEventListener('shopify:section:load', initAutoplay);
})();


/* ==========================================================================
   10. LIEN VERS UN ACCORDEON
   --------------------------------------------------------------------------
   Un lien dont l'adresse vise un accordeon (#mps-acc-…) l'ouvre en plus de
   faire defiler la page jusqu'a lui. Sans cela, le client arriverait sur
   un titre ferme et devrait cliquer une seconde fois.
   ========================================================================== */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href^="#mps-acc-"]');
    if (!link) return;
    var target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target || target.tagName !== 'DETAILS') return;
    event.preventDefault();
    target.open = true;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();


/* ==========================================================================
   11. COMPTE A REBOURS D'OFFRE LIMITEE
   --------------------------------------------------------------------------
   Lit la date de fin (data-end, en secondes) posee par le Liquid. Tous les
   visiteurs voient donc le meme compte a rebours : il ne repart pas a zero
   a chaque visite. La barre suit la part de la periode d'offre ecoulee
   (data-start -> data-end). A l'echeance, le bandeau disparait.
   ========================================================================== */
(function () {
  'use strict';

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function initCountdowns() {
    document.querySelectorAll('[data-mps-countdown]:not([data-mps-bound])').forEach(function (box) {
      box.dataset.mpsBound = 'true';
      var end = parseInt(box.dataset.end, 10) * 1000;
      var start = parseInt(box.dataset.start, 10) * 1000;
      var clock = box.querySelector('[data-mps-countdown-clock]');
      var bar = box.querySelector('[data-mps-countdown-bar]');
      if (!end) return;

      function tick() {
        var now = Date.now();
        var left = end - now;
        if (left <= 0) {
          box.hidden = true;
          clearInterval(timer);
          return;
        }
        var s = Math.floor(left / 1000);
        var days = Math.floor(s / 86400);
        var rest = pad(Math.floor(s % 86400 / 3600)) + ':' + pad(Math.floor(s % 3600 / 60)) + ':' + pad(s % 60);
        if (clock) clock.textContent = days > 0 ? days + ' d ' + rest : rest;
        if (bar && start && start < end) {
          var pct = Math.min(100, Math.max(0, (now - start) / (end - start) * 100));
          bar.style.width = pct.toFixed(1) + '%';
        }
      }

      var timer = setInterval(tick, 1000);
      tick();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCountdowns);
  } else {
    initCountdowns();
  }
  document.addEventListener('shopify:section:load', initCountdowns);
})();
